import http from "@services/httpService"

/**
 * Dashboard Ban lãnh đạo (Feature-0200). Backend: `ExecutiveDashboardAppService`
 * (`api/services/app/ExecutiveDashboard/*`). Mọi method là read-only.
 */

/** Widget W-EXE-02 — một dự án trong bảng so sánh. */
export interface IProjectComparisonRow {
  projectId: number
  projectName?: string
  /** Thực thu trong kỳ. */
  collectionMtd: number
  /** Kế hoạch thu trong kỳ — mẫu số của `collectionRate`. */
  collectionPlan: number
  /** `null` khi kỳ không có kế hoạch thu, KHÁC "đạt 0%" ⇒ hiển thị "—". */
  collectionRate?: number | null
  newContractValue: number
  overdueAmount: number
  availableUnits: number
  /** `null` khi không đo được, hoặc người dùng không được xem Phản ánh. */
  feedbackSlaRate?: number | null
}

/** Widget W-EXE-02 — bảng so sánh KPI theo dự án, đã sắp theo thực thu giảm dần từ backend. */
export interface IProjectComparison {
  rows: IProjectComparisonRow[]
  /** Số dự án trong phạm vi quyền — lớn hơn `rows.length` khi bảng bị cắt. */
  totalProjectCount: number
  /** `true` ⇒ bảng CHƯA đầy đủ (trần dòng vì nợ quá hạn quét riêng từng dự án). */
  isTruncated: boolean
  fromDate: string
  toDate: string
}

/** Widget W-EXE-01 — thẻ thu tiền, lấy điểm CUỐI của biểu đồ 6 tháng W-FIN-01. */
export interface IExecutiveCollectionCard {
  month: string
  actualAmount: number
  planAmount: number
  /** `null` khi kỳ không có kế hoạch thu — KHÁC "đạt 0%". */
  collectionRate?: number | null
}

/** Widget W-EXE-01 — thẻ doanh số, các bậc của funnel W-SAL-01. */
export interface IExecutiveSalesCard {
  reservedCount: number
  depositedCount: number
  contractCount: number
  depositCancelRate?: number | null
}

/** Widget W-EXE-01 — thẻ công nợ, điểm hiện tại của xu hướng aging W-FIN-03. */
export interface IExecutiveDebtCard {
  snapshotDate: string
  totalOverdue: number
  bucket30: number
  bucket60: number
  bucket90: number
  bucketOver90: number
  /** Vượt trần hợp đồng quét được ⇒ số ĐANG THIẾU. */
  isTruncated: boolean
}

/** Widget W-EXE-01 — thẻ tồn kho, nguồn W-SAL-05. */
export interface IExecutiveInventoryCard {
  totalUnit: number
  unitAvailable: number
  unitBooked: number
  unitDeposited: number
  unitSold: number
}

/** Widget W-EXE-01 — 4 số sức khoẻ hậu mãi; `null` = không đo được HOẶC thiếu quyền con. */
export interface IExecutiveCsHealthCard {
  feedbackResolutionSlaRate?: number | null
  pinkBookBacklogCount?: number | null
  pinkBookBacklogDays?: number | null
  handoverOnTimeRate?: number | null
  overdueFeeAmount?: number | null
  isOverdueFeeTruncated: boolean
}

/**
 * Widget W-EXE-01 — 5 nhóm KPI. Nhóm `null` nghĩa là người xem KHÔNG có quyền phân hệ đó; backend cố ý
 * không trả 403 cho cả trang (yêu cầu 4.1).
 */
export interface IExecutiveSummary {
  collection?: IExecutiveCollectionCard | null
  sales?: IExecutiveSalesCard | null
  debt?: IExecutiveDebtCard | null
  inventory?: IExecutiveInventoryCard | null
  csHealth?: IExecutiveCsHealthCard | null
}

/** Widget W-EXE-03 — một tháng của xu hướng 12 tháng. */
export interface IExecutiveTrendPoint {
  month: string
  collectionAmount: number
  newContractValue: number
  overdueAmount: number
  /** Điểm nợ bị cắt vì vượt trần quét ⇒ `overdueAmount` đang THIẾU. */
  isOverdueTruncated: boolean
}

class DashboardExecutiveService {
  public async getExecutiveTrend(
    params?: Record<string, unknown>
  ): Promise<IExecutiveTrendPoint[]> {
    const res = await http.get(
      "api/services/app/ExecutiveDashboard/GetExecutiveTrend",
      { params }
    )
    return res.data.result
  }

  public async getExecutiveSummary(
    params?: Record<string, unknown>
  ): Promise<IExecutiveSummary> {
    const res = await http.get(
      "api/services/app/ExecutiveDashboard/GetExecutiveSummary",
      { params }
    )
    return res.data.result
  }

  public async getProjectComparison(
    params?: Record<string, unknown>
  ): Promise<IProjectComparison> {
    const res = await http.get(
      "api/services/app/ExecutiveDashboard/GetProjectComparison",
      { params }
    )
    return res.data.result
  }
}

export default new DashboardExecutiveService()
